import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/taxtypelisting";

const AssetTaxMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  /* ---------------------------------- */
  /* localStorage helpers               */
  /* ---------------------------------- */
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ fetch list
  const fetchList = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        headers: commonHeaders,
      });

      const data = res.data?.data || [];

      const mapped = data.map((r) => ({
        id: r.fld_TaxId ?? r.taxId ?? r.id,
        taxType: r.fld_TaxType ?? r.taxType ?? r.taxtype,
        status:
          r.fld_Status === 0 ||
          r.fld_Status === false ||
          r.fld_IsActive === 0 ||
          r.fld_IsActive === false ||
          r.status === "Inactive"
            ? "Inactive"
            : "Active",
        selected: false,
      }));

      setRows(mapped);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Tax Type listing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected).length,
    [rows]
  );

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => r.selected),
    [rows]
  );

  const handleAddNew = () => {
    navigate("/Asset/AssetTaxMasterEntry");
  };

  const handleEdit = (id) => {
    navigate(`/Asset/AssetTaxMasterEntry?id=${id}`);
  };

  const toggleRow = (id) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r))
    );
  };

  const toggleAll = () => {
    const next = !allSelected;
    setRows((prev) => prev.map((r) => ({ ...r, selected: next })));
  };

  const handleDeleteSelected = async () => {
    const ids = rows.filter((r) => r.selected).map((r) => r.id);

    if (ids.length === 0) {
      toast.error("Please select records to delete!");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${API_BASE}/bulkDelete`,
        { ids },
        { headers: commonHeaders }
      );

      toast.success(res.data?.message || "Deleted successfully");
      await fetchList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return isDark
          ? "bg-green-800 text-green-100"
          : "bg-green-100 text-green-800";
      case "Inactive":
        return isDark
          ? "bg-red-800 text-red-100"
          : "bg-red-100 text-red-800";
      default:
        return isDark
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-200 text-gray-700";
    }
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TAX MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Tax Type Listing
            </h1>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={handleDeleteSelected}
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : isDark
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <FaTrash />
                Delete Selected {selectedCount > 0 ? `(${selectedCount})` : ""}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20 w-16">
                      Sl No
                    </th>

                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20 w-20">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="w-4 h-4 rounded"
                      />
                    </th>

                    <th className="px-6 py-3 text-center font-semibold border-r border-white/20">
                      Tax Type
                    </th>

                    <th className="px-6 py-3 text-center font-semibold border-r border-white/20">
                      Status
                    </th>

                    <th className="px-6 py-3 text-center font-semibold w-28">
                      Update
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`transition-colors duration-200 ${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-gray-50"
                          : isDark
                          ? "bg-gray-900"
                          : "bg-white"
                      }`}
                    >
                      <td
                        className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>

                      <td className="px-4 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                        <input
                          type="checkbox"
                          checked={!!r.selected}
                          onChange={() => toggleRow(r.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </td>

                      <td
                        className={`px-6 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                          isDark ? "text-blue-300" : "text-blue-600"
                        }`}
                      >
                        {r.taxType}
                      </td>

                      <td className="px-6 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            r.status
                          )}`}
                        >
                          {r.status}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-center">
                        <button
                          onClick={() => handleEdit(r.id)}
                          className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all ${
                            isDark
                              ? "bg-yellow-600 hover:bg-yellow-500 text-white"
                              : "bg-yellow-500 hover:bg-yellow-600 text-white"
                          }`}
                        >
                          <FaEdit />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className={`px-6 py-10 text-center ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No records found.
                      </td>
                    </tr>
                  )}

                  {loading && (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center">
                        Loading...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTaxMaster;